import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Modal } from "react-bootstrap";
import moment from "moment";
import Loader from "../../../components/loader/loader";
import Snackbar from "../../../components/snackbar/snackbar";
import {
  admin_previous_internships_detail,
  admin_previous_internships_update,
  institutions_list
} from "../admin.api";
import "./style.css";

export default function PreviousInternshipDetail(props) {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [isSnackError, setIsSnackError] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);

  // Edit Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [formFields, setFormFields] = useState({
    full_name: "",
    date_of_birth: "",
    gender: "male",
    address: "",
    email: "",
    phone_no: "",
    is_kerala_native: "1",
    course_completion_status: "completed",
    institution_id: "",
    institution_name: ""
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [recLetterFile, setRecLetterFile] = useState(null);

  useEffect(() => {
    if (id) {
      fetchDetail();
    }
  }, [id]);

  useEffect(() => {
    institutions_list()
      .then((res) => {
        if (res?.data) {
          setInstitutions(res.data);
        }
      })
      .catch((err) => console.error("Error loading institutions:", err));
  }, []);

  const fetchDetail = () => {
    setIsLoading(true);
    admin_previous_internships_detail(id)
      .then((res) => {
        if (res?.success && res?.data) {
          setData(res.data);
        } else {
          setSnackMessage("Failed to load application details");
          setIsSnackError(true);
          setSnackOpen(true);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching detail:", error);
        setSnackMessage("Error loading application details");
        setIsSnackError(true);
        setSnackOpen(true);
        setIsLoading(false);
      });
  };

  const snackbarClose = () => {
    setSnackMessage("");
    setIsSnackError(false);
    setSnackOpen(false);
  };

  const openEditModal = () => {
    if (!data) return;
    setFormFields({
      full_name: data.full_name || "",
      date_of_birth: data.date_of_birth || "",
      gender: data.gender ? String(data.gender).toLowerCase() : "male",
      address: data.address || "",
      email: data.email || "",
      phone_no: data.phone_no || "",
      is_kerala_native: data.is_kerala_native !== undefined && data.is_kerala_native !== null ? String(data.is_kerala_native) : "1",
      course_completion_status: data.course_completion_status || "completed",
      institution_id: data.institution_id || "",
      institution_name: data.institution_name || ""
    });
    setPhotoFile(null);
    setResumeFile(null);
    setRecLetterFile(null);
    setShowEditModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormFields((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files?.[0];
    if (!file) return;

    const maxBytes = 5 * 1024 * 1024;
    if (file.size > maxBytes) {
      setSnackMessage("File size must be under 5MB");
      setIsSnackError(true);
      setSnackOpen(true);
      e.target.value = "";
      return;
    }

    if (name === "photo") setPhotoFile(file);
    if (name === "resume") setResumeFile(file);
    if (name === "rec_letter_name") setRecLetterFile(file);
  };

  const handleUpdateSubmit = (e) => {
    e.preventDefault();

    if (!formFields.full_name || !formFields.date_of_birth || !formFields.address || !formFields.phone_no) {
      setSnackMessage("Please fill in all required fields (Name, DOB, Address, Phone No)");
      setIsSnackError(true);
      setSnackOpen(true);
      return;
    }

    setIsUpdating(true);

    const formData = new FormData();
    formData.append("_method", "PUT");
    formData.append("full_name", formFields.full_name);
    formData.append("date_of_birth", formFields.date_of_birth);
    formData.append("gender", formFields.gender);
    formData.append("address", formFields.address);
    formData.append("email", formFields.email);
    formData.append("phone_no", formFields.phone_no);
    formData.append("is_kerala_native", formFields.is_kerala_native);
    formData.append("course_completion_status", formFields.course_completion_status);
    if (formFields.institution_id) {
      formData.append("institution_id", formFields.institution_id);
    }
    formData.append("institution_name", formFields.institution_name);

    if (photoFile) formData.append("photo", photoFile);
    if (resumeFile) formData.append("resume", resumeFile);
    if (recLetterFile) formData.append("rec_letter_name", recLetterFile);

    admin_previous_internships_update(id, formData)
      .then((res) => {
        if (res?.success) {
          setSnackMessage("Record updated successfully");
          setIsSnackError(false);
          setSnackOpen(true);
          setShowEditModal(false);
          fetchDetail();
        } else {
          setSnackMessage(res.message || "Failed to update record");
          setIsSnackError(true);
          setSnackOpen(true);
        }
        setIsUpdating(false);
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message || "An error occurred";
        setSnackMessage(errMsg);
        setIsSnackError(true);
        setSnackOpen(true);
        setIsUpdating(false);
      });
  };

  if (isLoading) return <Loader open={true} />;

  if (!data) return (
    <div className="container mt-5">
      <div className="alert alert-warning">
        <p>Failed to load application details.</p>
        <button className="btn btn-primary btn-sm" onClick={() => props.history.push("/admin-previous-applications")}>Go Back</button>
      </div>
    </div>
  );

  return (
    <div className="registered-internships-page">
      <Loader open={isUpdating} />
      <Snackbar message={snackMessage} error={isSnackError} open={snackOpen} close={snackbarClose} />

      <div className="container-fluid pt-4 mb-5 px-md-5">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center gap-3">
            <button
              className="btn btn-minimal-outline btn-sm"
              onClick={() => props.history.push("/admin-previous-applications")}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              Back to List
            </button>
          </div>

          <div className="d-flex gap-2">
            <button className="btn btn-sm btn-outline-primary px-3" onClick={openEditModal}>
              Modify Details
            </button>
          </div>
        </div>

        <div className="row g-4">
          {/* Left Column: Summary Sidebar */}
          <div className="col-xl-3 col-lg-4 mb-4">
            <div className="section-card-minimal">
              <div className="section-header">Applicant Overview</div>
              <div className="card-body p-4 text-center">
                <div className="mb-4">
                  {data.file_upload_photo ? (
                    <img
                      src={data.file_upload_photo}
                      alt="Profile"
                      className="profile-img-minimal"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/assets/img/dummy.png";
                      }}
                    />
                  ) : (
                    <div className="profile-img-minimal bg-light d-inline-flex align-items-center justify-content-center mx-auto">
                      <span className="h1 text-muted mb-0">{data.full_name?.charAt(0)}</span>
                    </div>
                  )}
                </div>
                <h5 className="fw-bold mb-1">{data.full_name}</h5>
                <p className="small text-muted mb-4 opacity-75">Ref ID: {data.id}</p>

                <div className="text-start">
                  <div className="info-item-minimal mb-3">
                    <label>Email Address</label>
                    <span className="text-break">{data.email || "N/A"}</span>
                  </div>
                  <div className="info-item-minimal mb-3">
                    <label>Contact Number</label>
                    <span>{data.phone_no}</span>
                  </div>
                  <div className="info-item-minimal mb-3">
                    <label>Application Date</label>
                    <span>{moment(data.created_at).format("DD MMM YYYY, hh:mm A")}</span>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-top">
                  {data.resume ? (
                    <a href={data.resume} target="_blank" rel="noreferrer" className="btn btn-minimal-dark w-100 mb-2">
                      View CV / Resume
                    </a>
                  ) : (
                    <span className="text-muted small d-block mb-2">No CV / Resume uploaded</span>
                  )}
                  {data.rec_letter_name ? (
                    <a href={data.rec_letter_name} target="_blank" rel="noreferrer" className="btn btn-minimal-dark w-100">
                      View Rec. Letter
                    </a>
                  ) : (
                    <span className="text-muted small d-block">No Recommendation Letter uploaded</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Detailed Info */}
          <div className="col-xl-9 col-lg-8">
            <div className="section-card-minimal">
              <div className="section-header">Candidate Profile</div>
              <div className="info-grid-minimal">
                <div className="info-item-minimal">
                  <label>Gender</label>
                  <span className="text-capitalize">{data.gender || "Not specified"}</span>
                </div>
                <div className="info-item-minimal">
                  <label>Date of Birth</label>
                  <span>{data.date_of_birth ? moment(data.date_of_birth).format("DD MMM YYYY") : "N/A"}</span>
                </div>
                <div className="info-item-minimal">
                  <label>Native of Kerala</label>
                  <span>{data.is_kerala_native ? "Yes" : "No"}</span>
                </div>
                <div className="info-item-minimal">
                  <label>Course Completion Track</label>
                  <span className="text-capitalize">{data.course_completion_status || "N/A"}</span>
                </div>
                <div className="info-item-minimal col-span-2">
                  <label>Residential Address</label>
                  <span>{data.address || "N/A"}</span>
                </div>
              </div>
            </div>

            <div className="section-card-minimal mt-4">
              <div className="section-header">Academic Details</div>
              <div className="info-grid-minimal">
                <div className="info-item-minimal col-span-2">
                  <label>Institution Name</label>
                  <span>{data.institution_name || "N/A"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* EDIT MODAL */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <form onSubmit={handleUpdateSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Modify Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row">
              <div className="col-md-6 form-group mb-3">
                <label className="form-label">Full Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  name="full_name"
                  className="form-control"
                  value={formFields.full_name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="col-md-6 form-group mb-3">
                <label className="form-label">Date of Birth <span className="text-danger">*</span></label>
                <input
                  type="date"
                  name="date_of_birth"
                  className="form-control"
                  value={formFields.date_of_birth}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="col-md-6 form-group mb-3">
                <label className="form-label">Gender</label>
                <select name="gender" className="form-select form-control" value={formFields.gender} onChange={handleInputChange}>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="col-md-6 form-group mb-3">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  value={formFields.email}
                  onChange={handleInputChange}
                />
              </div>

              <div className="col-md-6 form-group mb-3">
                <label className="form-label">Phone No <span className="text-danger">*</span></label>
                <input
                  type="text"
                  name="phone_no"
                  className="form-control"
                  value={formFields.phone_no}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="col-md-6 form-group mb-3">
                <label className="form-label">Kerala Native</label>
                <select name="is_kerala_native" className="form-select form-control" value={formFields.is_kerala_native} onChange={handleInputChange}>
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
              </div>

              <div className="col-md-6 form-group mb-3">
                <label className="form-label">Course Completion Status</label>
                <select
                  name="course_completion_status"
                  className="form-select form-control"
                  value={formFields.course_completion_status}
                  onChange={handleInputChange}
                >
                  <option value="completed">Completed</option>
                  <option value="undergoing">Undergoing</option>
                </select>
              </div>

              <div className="col-md-6 form-group mb-3">
                <label className="form-label">Academic Institution</label>
                <select
                  name="institution_id"
                  className="form-select form-control"
                  value={formFields.institution_id}
                  onChange={(e) => {
                    const instId = e.target.value;
                    const inst = institutions.find(i => String(i.id) === String(instId));
                    setFormFields((prev) => ({
                      ...prev,
                      institution_id: instId,
                      institution_name: inst ? inst.institution_name : ""
                    }));
                  }}
                >
                  <option value="">Select Institution</option>
                  {institutions.map((inst) => (
                    <option key={inst.id} value={inst.id}>
                      {inst.institution_name}
                    </option>
                  ))}
                  <option value="other">Other / Custom</option>
                </select>
              </div>

              {(!formFields.institution_id || formFields.institution_id === "other") && (
                <div className="col-md-12 form-group mb-3">
                  <label className="form-label">Custom Institution Name</label>
                  <input
                    type="text"
                    name="institution_name"
                    className="form-control"
                    placeholder="Enter Custom Institution Name"
                    value={formFields.institution_name}
                    onChange={handleInputChange}
                  />
                </div>
              )}

              <div className="col-md-12 form-group mb-3">
                <label className="form-label">Address <span className="text-danger">*</span></label>
                <textarea
                  name="address"
                  className="form-control"
                  rows="2"
                  value={formFields.address}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="col-md-4 form-group mb-3">
                <label className="form-label">Photo (Update)</label>
                <input
                  type="file"
                  name="photo"
                  className="form-control"
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </div>

              <div className="col-md-4 form-group mb-3">
                <label className="form-label">Resume (Update)</label>
                <input
                  type="file"
                  name="resume"
                  className="form-control"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                />
              </div>

              <div className="col-md-4 form-group mb-3">
                <label className="form-label">Rec. Letter (Update)</label>
                <input
                  type="file"
                  name="rec_letter_name"
                  className="form-control"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isUpdating}>
              Save Changes
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
}
